import { FC, useMemo } from "react";

import { ChevronRightIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { HStack, Select, useMultiStyleConfig } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { IconButton } from "@components/buttons";

import { TCustomHeaderParams, TRenderCustomHeaderArgs } from "./types";
import checkYearBoundaries from "./utils/checkYearBoundaries";
import getMonths from "./utils/getMonths";
import getYears from "./utils/getYears";

const CustomHeader: FC<TCustomHeaderParams & TRenderCustomHeaderArgs> = ({
  id,
  decreaseMonth,
  date,
  prevMonthButtonDisabled,
  increaseMonth,
  changeYear,
  changeMonth,
  nextMonthButtonDisabled,
  locale,
  minDate,
  maxDate,
  selectYearFrom = 1990,
  selectYearTo = 2050,
}) => {
  const styles = useMultiStyleConfig("DatePicker", {});
  const years = useMemo(
    () => getYears(selectYearFrom, selectYearTo, { minDate, maxDate }),
    [selectYearFrom, selectYearTo, minDate, maxDate]
  );
  const isPrevDisabled = prevMonthButtonDisabled || !checkYearBoundaries(years, date, "reduce");
  const isNextDisabled = nextMonthButtonDisabled || !checkYearBoundaries(years, date, "increase");
  const months = useMemo(
    () => getMonths(locale, { selectedYear: date.getFullYear(), minDate, maxDate }),
    [date, locale, maxDate, minDate]
  );
  const { t } = useTranslation();

  const handleYearChange = ({ target }) => {
    if (!target.value) {
      throw new Error("Date change value not found");
    }
    const year = parseInt(target.value, 10);

    changeYear(year);
  };

  const handleMonthChange = ({ target }) => {
    if (!target.value) {
      throw new Error("Date change value not found");
    }
    const month = parseInt(target.value, 10);

    changeMonth(month);
  };
  return (
    <HStack justifyContent="space-between">
      <IconButton
        variant="transparent"
        icon={<ChevronLeftIcon sx={styles.chevronIcon} />}
        onClick={decreaseMonth}
        sx={styles.dateSelectButton}
        isDisabled={isPrevDisabled}
        title={t("common-date_picker-previous_month")}
      />
      <HStack spacing="0.5rem" sx={styles.dateSelectWrapper}>
        <Select
          variant="unstyled"
          id={id && `${id}-month`}
          value={date.getMonth()}
          onChange={handleMonthChange}
          onBlur={(e) => e.preventDefault()}
        >
          {months.map(({ value, name }) => (
            <option key={`${name}-${value}`} value={value}>
              {name}
            </option>
          ))}
        </Select>
        <Select
          variant="unstyled"
          id={id && `${id}-year`}
          value={date.getFullYear()}
          onChange={handleYearChange}
          onBlur={(e) => e.preventDefault()}
        >
          {years.map((option) => (
            <option key={`datepicker-year-option-${option}`} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </HStack>
      <IconButton
        variant="transparent"
        icon={<ChevronRightIcon sx={styles.chevronIcon} />}
        onClick={increaseMonth}
        sx={styles.dateSelectButton}
        isDisabled={isNextDisabled}
        title={t("common-date_picker-next_month")}
      />
    </HStack>
  );
};
export default CustomHeader;
